import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  alpha,
  useTheme,
} from '@mui/material';
import { Add as AddIcon } from '@mui/icons-material';
import ScheduledRouteCard from './ScheduledRouteCard';
import LoadingSkeleton from '../LoadingSkeleton';
import { elevatedPanelSx } from '../../constants/shape';
import { useAuthPermissions } from '../../hooks/useAuthPermissions';
import { listCollectionPoints } from '../../services/collection-point.service';
import { listSuppliers } from '../../services/supplier.service';
import { useOrganizationStore } from '../../stores/organization.store';
import { useRouteScheduleStore } from '../../stores/routeSchedule.store';
import { useRouteStore } from '../../stores/route.store';
import type { Route } from '../../types/route.type';
import type { DayOfWeek, RouteScheduleDay } from '../../types/routeSchedule.type';
import type { SupplierCollectionPoint } from '../../types/supplier.type';
import {
  findScheduleDayEntries,
  getCurrentWeekRange,
  getDateForDayInWeek,
  resolveOrderedCollectionPoints,
  resolveScheduleDayLabelKey,
  resolveScheduleTabViewState,
  scheduleMatchesTruck,
  SCHEDULE_DAY_DISPLAY_ORDER,
  type ScheduleCollectionPointSummary,
} from './truckRouteScheduleTab.helpers';

const dateFormatter = new Intl.DateTimeFormat(undefined, { day: 'numeric', month: 'short' });

function routeSecondaryLabel(route: Route, t: (key: string, options?: Record<string, unknown>) => string): string {
  const stopLabel = t('transportation.fleet.details.schedule.stopCount', { count: route.stop_count });
  return [route.city, stopLabel].filter(Boolean).join(' · ');
}

interface DayCellProps {
  day: DayOfWeek;
  entries: RouteScheduleDay[];
  error: string | null;
  isPending: boolean;
  isOpen: boolean;
  canEdit: boolean;
  activeRoutes: Route[];
  routesLoading: boolean;
  truckId: string;
  resolveCollectionPoints: (route: Route) => ScheduleCollectionPointSummary[];
  collectionPointsLoading: boolean;
  onOpen: () => void;
  onClose: () => void;
  /** Appends `routeId` to this day's existing ordered list. */
  onPick: (routeId: string) => void;
  /** Removes just `routeId` from this day's list -- other routes that day are unaffected. */
  onRemove: (routeId: string) => void;
  /** Swaps the entry at `index` with its neighbor in `direction` within this day's list. */
  onMove: (index: number, direction: -1 | 1) => void;
  onDismissError: () => void;
  onViewRoute: (routeId: string) => void;
}

function DayCell({
  entries,
  error,
  isPending,
  isOpen,
  canEdit,
  activeRoutes,
  routesLoading,
  truckId,
  resolveCollectionPoints,
  collectionPointsLoading,
  onOpen,
  onClose,
  onPick,
  onRemove,
  onMove,
  onDismissError,
  onViewRoute,
}: DayCellProps) {
  const { t } = useTranslation();
  const theme = useTheme();
  const isFilled = entries.length > 0;
  // The "Add route" affordance only ever offers routes not already scheduled on this day --
  // picking is strictly additive (appends one more route), never a replace.
  const availableRoutes = activeRoutes.filter(
    (route) => !entries.some((entry) => entry.route.id === route.id),
  );

  return (
    <TableCell
      sx={{
        verticalAlign: 'top',
        width: `${100 / 7}%`,
        p: 1,
        borderLeft: '1px solid',
        borderColor: 'divider',
        transition: 'background-color 0.1s',
        // Explicit, low-opacity tint rather than the theme's `action.hover` token -- this
        // app's custom theme resolves that to a solid, unrelated brown, not a subtle overlay.
        '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.06) },
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 0.75,
          height: '100%',
          minHeight: 72,
          // Filled/open cells flow top-down; an empty cell's "Add route" affordance (or the
          // read-only em-dash) centers within the full cell height instead of sitting at the
          // top, since every cell in this shared table row stretches to match the tallest one.
          justifyContent: isFilled ? 'flex-start' : 'center',
          alignItems: isFilled ? 'stretch' : 'center',
        }}
      >
        {error ? (
          <Alert severity="error" variant="outlined" sx={{ py: 0 }} onClose={onDismissError}>
            <Typography variant="caption">{error}</Typography>
          </Alert>
        ) : null}

        {isPending ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
            <CircularProgress size={20} />
          </Box>
        ) : (
          <>
            {entries.map((entry, index) => (
              <ScheduledRouteCard
                key={entry.route.id}
                route={entry.route}
                viewerTruckId={truckId}
                canEdit={canEdit}
                onClear={() => onRemove(entry.route.id)}
                onViewRoute={() => onViewRoute(entry.route.id)}
                collectionPoints={resolveCollectionPoints(entry.route)}
                collectionPointsLoading={collectionPointsLoading}
                // Reorder controls are only meaningful once a day has 2+ routes -- omitted
                // entirely (rather than passed-but-disabled) for a single-route day.
                onMoveUp={entries.length > 1 ? () => onMove(index, -1) : undefined}
                onMoveDown={entries.length > 1 ? () => onMove(index, 1) : undefined}
                canMoveUp={index > 0}
                canMoveDown={index < entries.length - 1}
              />
            ))}

            {canEdit && isOpen ? (
              <Autocomplete
                size="small"
                fullWidth
                openOnFocus
                options={availableRoutes}
                loading={routesLoading}
                getOptionLabel={(option) => option.name}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                onChange={(_, value) => {
                  if (value) onPick(value.id);
                }}
                onClose={onClose}
                renderOption={(props, option) => (
                  <Box component="li" {...props} key={option.id}>
                    <Box sx={{ minWidth: 0 }}>
                      <Typography variant="body2" noWrap>
                        {option.name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" noWrap>
                        {routeSecondaryLabel(option, t)}
                      </Typography>
                    </Box>
                  </Box>
                )}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    autoFocus
                    placeholder={t('transportation.fleet.details.schedule.pickerPlaceholder')}
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {routesLoading ? <CircularProgress color="inherit" size={16} /> : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
              />
            ) : canEdit && availableRoutes.length > 0 ? (
              <Button
                size="small"
                variant="outlined"
                startIcon={<AddIcon fontSize="small" />}
                onClick={onOpen}
                sx={{ textTransform: 'none' }}
              >
                {t('transportation.fleet.details.schedule.addRoute')}
              </Button>
            ) : entries.length === 0 ? (
              <Typography variant="caption" color="text.secondary">
                &mdash;
              </Typography>
            ) : null}
          </>
        )}
      </Box>
    </TableCell>
  );
}

export interface TruckRouteScheduleTabProps {
  truckId: string;
}

/**
 * Route Schedule tab of `TruckDetails.tsx`: a single-row calendar table, one
 * column per day (Sunday-first), showing this truck's weekly route
 * assignments. All 7 days always sit in one row (the table scrolls
 * horizontally on narrow viewports rather than wrapping to a card grid).
 * Each cell owns its own pending/error state so one failing day never blocks
 * the others; picking, reordering, or removing a route fires immediately, no
 * page-level Save button. A day can now hold 2+ ordered routes (Task 3):
 * each `DayCell` renders its day's routes as an ordered list of
 * `ScheduledRouteCard`s (with arrow reorder + per-route remove) plus an
 * "Add route" affordance to append one more, rather than the old
 * single-route-or-empty cell.
 */
export default function TruckRouteScheduleTab({ truckId }: TruckRouteScheduleTabProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const selectedOrganization = useOrganizationStore((state) => state.selectedOrganization);
  const { canMutate } = useAuthPermissions();
  const {
    schedule,
    isLoading,
    error,
    dayErrors,
    fetchTruckSchedule,
    setDay,
    clearDay,
    clearDayError,
  } = useRouteScheduleStore();
  const { routes, isLoading: routesLoading, fetchRoutes } = useRouteStore();

  const [pendingDays, setPendingDays] = useState<Set<DayOfWeek>>(new Set());
  const [openDay, setOpenDay] = useState<DayOfWeek | null>(null);
  const [collectionPointsById, setCollectionPointsById] = useState<Map<string, SupplierCollectionPoint>>(new Map());
  const [supplierNamesById, setSupplierNamesById] = useState<Map<string, string>>(new Map());
  const [collectionPointsLoading, setCollectionPointsLoading] = useState(false);

  const orgId = selectedOrganization?.id;
  const canEdit = Boolean(orgId && canMutate(`recyclee.${orgId}.*.route_schedule.write`));

  useEffect(() => {
    if (truckId) void fetchTruckSchedule(truckId);
  }, [truckId, fetchTruckSchedule]);

  useEffect(() => {
    if (!orgId) return;
    void fetchRoutes({ organization_id: orgId, status: 'active', limit: 100 });
  }, [orgId, fetchRoutes]);

  // Org-wide, fetched once and shared across every day's cell -- each cell resolves its own
  // route's stops (+ each stop's supplier name) from these maps (see
  // `resolveOrderedCollectionPoints`), rather than one fetch per filled day. Same
  // collection-points service/pattern as `RouteDetails.tsx`'s stops table; a failure here
  // degrades to "no stops shown" rather than blocking the schedule itself, since it's a
  // secondary enrichment, not core to the tab functioning. `allSettled`, not `all` -- these two
  // fetches must never take each other down: a broken suppliers call should still leave stops
  // (sans supplier line) showing, not wipe out otherwise-good collection-point data.
  useEffect(() => {
    if (!orgId) return;
    setCollectionPointsLoading(true);
    void Promise.allSettled([
      listCollectionPoints({ organization_id: orgId, limit: 500 }),
      listSuppliers({ organization_id: orgId, limit: 500 }),
    ])
      .then(([pointsSettled, suppliersSettled]) => {
        setCollectionPointsById(
          pointsSettled.status === 'fulfilled'
            ? new Map(pointsSettled.value.collection_points.map((point) => [point.id, point]))
            : new Map(),
        );
        setSupplierNamesById(
          suppliersSettled.status === 'fulfilled'
            ? new Map(suppliersSettled.value.suppliers.map((supplier) => [supplier.id, supplier.name]))
            : new Map(),
        );
      })
      .finally(() => setCollectionPointsLoading(false));
  }, [orgId]);

  const activeRoutes = useMemo(() => routes.filter((route) => route.status === 'active'), [routes]);

  // Presentation only -- see `getCurrentWeekRange`'s docstring. Recomputed only if the
  // component stays mounted across a real day change, not on every render.
  const weekRange = useMemo(() => getCurrentWeekRange(), []);

  const markPending = (day: DayOfWeek, pending: boolean) => {
    setPendingDays((prev) => {
      const next = new Set(prev);
      if (pending) next.add(day);
      else next.delete(day);
      return next;
    });
  };

  // Appends `routeId` to `entries`' existing order and sends the day's whole new ordered list --
  // `setDay` has no diffing of its own (see its store docstring), so every mutation here
  // recomputes the full list first.
  const handlePick = async (day: DayOfWeek, entries: RouteScheduleDay[], routeId: string) => {
    setOpenDay(null);
    const routeIds = [...entries.map((entry) => entry.route.id), routeId];
    markPending(day, true);
    await setDay(truckId, day, routeIds);
    markPending(day, false);
  };

  // Removes just `routeId` from `entries`. A day's last remaining route can't be "set" to an
  // empty list (the backend rejects `route_ids: []` with 422 -- see `setScheduleDay`'s
  // docstring), so removing the only route on a day clears the whole day instead via `clearDay`.
  const handleRemove = async (day: DayOfWeek, entries: RouteScheduleDay[], routeId: string) => {
    const routeIds = entries.filter((entry) => entry.route.id !== routeId).map((entry) => entry.route.id);
    markPending(day, true);
    if (routeIds.length === 0) {
      await clearDay(truckId, day);
    } else {
      await setDay(truckId, day, routeIds);
    }
    markPending(day, false);
  };

  // Swaps the route at `index` with its neighbor in `direction` (same adjacent-swap idiom as
  // `RouteBuilder.tsx`'s `movePoint`) and persists the resulting order.
  const handleMove = async (day: DayOfWeek, entries: RouteScheduleDay[], index: number, direction: -1 | 1) => {
    const target = index + direction;
    if (target < 0 || target >= entries.length) return;
    const routeIds = entries.map((entry) => entry.route.id);
    [routeIds[index], routeIds[target]] = [routeIds[target], routeIds[index]];
    markPending(day, true);
    await setDay(truckId, day, routeIds);
    markPending(day, false);
  };

  // `schedule` is a module-level store singleton, so it can still hold a *different* truck's
  // data (from before this component's fetch for `truckId` resolves, or a lingering previous
  // mount). Always compare `schedule.truck_id` against this truck's own `truckId` prop rather
  // than treating any non-null `schedule` as "this truck's data" -- see `scheduleMatchesTruck`.
  const scheduleTruckId = schedule?.truck_id ?? null;

  const viewState = resolveScheduleTabViewState({
    isLoading,
    hasError: Boolean(error),
    truckId,
    scheduleTruckId,
  });

  if (viewState === 'loading') {
    return <LoadingSkeleton variant="table" header={false} filters={false} columns={7} rows={1} />;
  }

  if (viewState === 'error') {
    return (
      <Alert
        severity="error"
        action={
          <Button color="inherit" onClick={() => void fetchTruckSchedule(truckId)}>
            {t('common.retry')}
          </Button>
        }
      >
        {error || t('transportation.fleet.details.schedule.loadError')}
      </Alert>
    );
  }

  // Defense in depth alongside the `viewState` gate above: even if this branch were ever
  // reached with a stale, mismatched `schedule` (e.g. a future refactor of the gate above),
  // never render another truck's days as if they belonged to this one.
  const days = schedule && scheduleMatchesTruck(scheduleTruckId, truckId) ? schedule.days : [];

  // Shared by every `ScheduledRouteCard` this tab renders -- resolves one route's ordered stops
  // (+ each stop's supplier name) from the org-wide maps fetched above, rather than each card
  // (now potentially several per day) redoing this join inline.
  const resolveCardCollectionPoints = (route: Route): ScheduleCollectionPointSummary[] =>
    resolveOrderedCollectionPoints(route.collection_point_ids, collectionPointsById).map((point) => ({
      id: point.id,
      name: point.name,
      supplierName: supplierNamesById.get(point.supplier_id),
    }));

  return (
    <Box>
      {activeRoutes.length === 0 && !routesLoading ? (
        <Alert
          severity="info"
          sx={{ mb: 2 }}
          action={
            <Button color="inherit" size="small" onClick={() => navigate('/transportation/routes/new')}>
              {t('transportation.fleet.details.schedule.createRoute')}
            </Button>
          }
        >
          {t('transportation.fleet.details.schedule.noActiveRoutes')}
        </Alert>
      ) : null}

      <Paper elevation={1} sx={{ ...elevatedPanelSx, overflow: 'hidden' }}>
        <TableContainer>
          <Table sx={{ tableLayout: 'fixed', minWidth: 7 * 150 }}>
            <TableHead>
              <TableRow>
                {SCHEDULE_DAY_DISPLAY_ORDER.map((day) => (
                  <TableCell
                    key={day}
                    align="center"
                    sx={{ width: `${100 / 7}%`, borderLeft: '1px solid', borderColor: 'divider' }}
                  >
                    <Typography
                      variant="caption"
                      fontWeight={700}
                      color="text.secondary"
                      sx={{ textTransform: 'uppercase', letterSpacing: 0.4, display: 'block' }}
                    >
                      {t(resolveScheduleDayLabelKey(day))}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {dateFormatter.format(getDateForDayInWeek(weekRange.start, day))}
                    </Typography>
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              <TableRow sx={{ '&:hover': { bgcolor: 'transparent' } }}>
                {SCHEDULE_DAY_DISPLAY_ORDER.map((day) => {
                  const entries = findScheduleDayEntries(days, day);
                  return (
                    <DayCell
                      key={day}
                      day={day}
                      entries={entries}
                      error={dayErrors[day]}
                      isPending={pendingDays.has(day)}
                      isOpen={openDay === day}
                      canEdit={canEdit}
                      activeRoutes={activeRoutes}
                      routesLoading={routesLoading}
                      truckId={truckId}
                      resolveCollectionPoints={resolveCardCollectionPoints}
                      collectionPointsLoading={collectionPointsLoading}
                      onOpen={() => setOpenDay(day)}
                      onClose={() => setOpenDay((current) => (current === day ? null : current))}
                      onPick={(routeId) => void handlePick(day, entries, routeId)}
                      onRemove={(routeId) => void handleRemove(day, entries, routeId)}
                      onMove={(index, direction) => void handleMove(day, entries, index, direction)}
                      onDismissError={() => clearDayError(day)}
                      onViewRoute={(routeId) => navigate(`/transportation/routes/${routeId}`)}
                    />
                  );
                })}
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </Box>
  );
}
